import React, { useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { getAssumedUser } from 'lib/auth/impersonation'
import Box from '@material-ui/core/Box'
import LinearProgress from '@material-ui/core/LinearProgress'
import Typography from '@material-ui/core/Typography'
import { PageHeader } from 'components/page-header'
import StarIcon from 'components/material-symbol/icons/Star'
import Error from 'components/error'
import UpgradeSuccessModal from 'components/upgrade-success-modal'
import { useUpgradeFlow } from 'lib/hooks/use-upgrade-flow'
import {
  COACH_DASHBOARD_PATH,
  parseBenchmarkMetricId,
} from 'pages/pga-coach/dashboard/performance-benchmarks/constants'
import CoachPlusPromoBanner, {
  useCoachPlusPromoStyles,
} from 'pages/pga-coach/dashboard/performance-benchmarks/benchmark-promo'
import { TopCoachesSummary } from 'pages/pga-coach/dashboard/performance-benchmarks/top-coaches-definition'
import BenchmarkDetailCard from './benchmark-detail-card'
import BottleneckInsight, {
  BottleneckPrimaryAction,
  ClearBookingUrlDialog,
  UPGRADE_SOURCE,
  getAvailablePlays,
  getVisiblePlays,
  isUpgradePlay,
  useBottleneckPlayActions,
} from './bottleneck-insight'
import useCoachBenchmarks from './use-coach-benchmarks'
import useStyles from './styles'

const BenchmarksPage = () => {
  const classes = useStyles({ assumedUser: getAssumedUser() })
  const promoClasses = useCoachPlusPromoStyles()
  const { search } = useLocation()
  const expandedMetricId = parseBenchmarkMetricId(search)
  const upgradeFlow = useUpgradeFlow('pga-coach-benchmarks', {
    source: UPGRADE_SOURCE,
    trackPromptShown: false,
  })
  const { hasPaidTier, trackPromptShown } = upgradeFlow
  const {
    bottleneck: coachBottleneck,
    metrics,
    shareUrl,
    cohortSize,
    leadAutoReplyEnabled,
    bookingUrl,
    loading,
    error,
  } = useCoachBenchmarks()
  const bottleneck = useMemo(
    () =>
      coachBottleneck && {
        ...coachBottleneck,
        plays: getAvailablePlays(coachBottleneck.plays, {
          hasPaidTier,
          bookingUrl,
        }),
      },
    [coachBottleneck, hasPaidTier, bookingUrl],
  )
  const visiblePlays = getVisiblePlays(bottleneck?.plays, shareUrl)
  const showsUpgradePlay = visiblePlays.some(isUpgradePlay)
  const {
    runPlay,
    busy,
    clearBookingUrlDialogProps,
  } = useBottleneckPlayActions({ upgradeFlow, bookingUrl })

  useEffect(() => {
    if (showsUpgradePlay) {
      trackPromptShown()
    }
  }, [showsUpgradePlay, trackPromptShown])

  useEffect(() => {
    if (!expandedMetricId || loading) {
      return
    }

    const element = document.getElementById(
      `benchmark-metric-${expandedMetricId}`,
    )
    element?.scrollIntoView?.({ block: 'start' })
  }, [expandedMetricId, loading])

  if (error) {
    return <Error error={error} />
  }

  return (
    <>
      <PageHeader
        title="Performance benchmarks"
        backTo={COACH_DASHBOARD_PATH}
      />
      {loading ? <LinearProgress color="primary" /> : null}
      <Box className={classes.container}>
        {!loading && !bottleneck ? (
          <Typography className={classes.sampleNotice}>
            We don't have benchmarks for your account yet. They appear once
            you're set up to take lessons on PGA Coach and have some lesson
            activity.
          </Typography>
        ) : null}

        {bottleneck ? (
          <>
            <CoachPlusPromoBanner
              className={classes.promoBanner}
              variant="coachPlus"
              eyebrow="Fastest win"
              eyebrowIcon={StarIcon}
              title={bottleneck.title}
              body={bottleneck.body}
              action={
                <BottleneckPrimaryAction
                  className={promoClasses.actionButton}
                  bottleneck={bottleneck}
                  shareUrl={shareUrl}
                  onRunPlay={runPlay}
                  busy={busy}
                  leadAutoReplyEnabled={leadAutoReplyEnabled}
                />
              }
            />
            {visiblePlays.length ? (
              <Box className={classes.metricListCard}>
                <BottleneckInsight
                  bottleneck={bottleneck}
                  shareUrl={shareUrl}
                  onRunPlay={runPlay}
                  busy={busy}
                  leadAutoReplyEnabled={leadAutoReplyEnabled}
                />
              </Box>
            ) : null}
          </>
        ) : null}

        {metrics.length ? (
          <Box className={classes.metricListCard}>
            {metrics.map(metric => (
              <BenchmarkDetailCard
                key={metric.id}
                metric={metric}
                classes={classes}
                cohortSize={cohortSize}
                defaultExpanded={metric.id === expandedMetricId}
              />
            ))}
          </Box>
        ) : null}

        {bottleneck ? (
          <TopCoachesSummary cohortSize={cohortSize} className={classes.footer}>
            Figures are anonymized medians.
          </TopCoachesSummary>
        ) : null}
      </Box>
      <ClearBookingUrlDialog {...clearBookingUrlDialogProps} />
      <UpgradeSuccessModal
        isOpen={upgradeFlow.showSuccessModal}
        onClose={upgradeFlow.handleDismiss}
      />
    </>
  )
}

export default BenchmarksPage
